import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { getToken } from '../api/client';
import { config } from '../config';
import { useTheme } from '../context/ThemeContext';
import { exportCsvFromUrl } from '../utils/fileExport';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

interface FilterOption {
  readonly label: string;
  readonly value: string;
}

/** Props for AdminToolbar - search, pagination, CSV export, and optional filter chips. */
interface AdminToolbarProps {
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly onSearch: () => void;
  readonly total: number;
  readonly page: number;
  readonly limit: number;
  readonly onPageChange: (p: number) => void;
  readonly csvEndpoint: string;
  readonly csvFilename: string;
  readonly label: string;
  readonly filterOptions?: FilterOption[];
  readonly activeFilter?: string;
  readonly onFilterChange?: (value: string) => void;
}

/** AdminToolbar - search bar, filter chips, pagination, and CSV export for admin list views. */
export function AdminToolbar({ search, onSearchChange, onSearch, total, page, limit, onPageChange, csvEndpoint, csvFilename, label, filterOptions, activeFilter, onFilterChange }: AdminToolbarProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [exporting, setExporting] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const token = getToken();
      const separator = csvEndpoint.includes('?') ? '&' : '?';
      const url = `${config.apiUrl}${csvEndpoint}${separator}format=csv`;
      await exportCsvFromUrl(url, { Authorization: `Bearer ${token}` }, csvFilename);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [csvEndpoint, csvFilename]);

  return (
    <View>
      <View style={styles.topRow}>
        <View style={styles.searchWrap}>
          <Ionicons name="search" size={18} color={colors.textLight} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search..."
            placeholderTextColor={colors.textLight}
            value={search}
            onChangeText={onSearchChange}
            onSubmitEditing={onSearch}
            returnKeyType="search"
            maxLength={100}
          />
          {search ? (
            <TouchableOpacity onPress={() => { onSearchChange(''); setTimeout(onSearch, 0); }} style={styles.clearBtn}>
              <Ionicons name="close-circle" size={18} color={colors.textLight} />
            </TouchableOpacity>
          ) : null}
        </View>
        <TouchableOpacity onPress={handleExportCsv} style={styles.exportBtn} disabled={exporting}>
          <Ionicons name="download-outline" size={18} color={colors.white} />
          <Text style={styles.exportText}>{exporting ? '...' : 'CSV'}</Text>
        </TouchableOpacity>
      </View>

      {filterOptions && onFilterChange && (
        <View style={styles.filterRow}>
          <TouchableOpacity
            onPress={() => onFilterChange('')}
            style={[styles.chip, !activeFilter && styles.chipActive]}
          >
            <Text style={[styles.chipText, !activeFilter && styles.chipTextActive]}>All</Text>
          </TouchableOpacity>
          {filterOptions.map(opt => (
            <TouchableOpacity
              key={opt.value}
              onPress={() => onFilterChange(opt.value)}
              style={[styles.chip, activeFilter === opt.value && styles.chipActive]}
            >
              <Text style={[styles.chipText, activeFilter === opt.value && styles.chipTextActive]}>{opt.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      <View style={styles.metaRow}>
        <Text style={styles.countText}>{total} {label}{total !== 1 ? 's' : ''}</Text>
        {totalPages > 1 && (
          <View style={styles.paginationInline}>
            <TouchableOpacity disabled={page <= 1} onPress={() => onPageChange(page - 1)} hitSlop={8}>
              <Ionicons name="chevron-back" size={20} color={page <= 1 ? colors.border : colors.primary} />
            </TouchableOpacity>
            <Text style={styles.pageText}>{page}/{totalPages}</Text>
            <TouchableOpacity disabled={page >= totalPages} onPress={() => onPageChange(page + 1)} hitSlop={8}>
              <Ionicons name="chevron-forward" size={20} color={page >= totalPages ? colors.border : colors.primary} />
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  topRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm },
  searchWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border, paddingHorizontal: spacing.sm },
  searchIcon: { marginRight: spacing.xs },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: fontSize.sm, color: c.text },
  clearBtn: { padding: 4 },
  exportBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.primary, borderRadius: borderRadius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, gap: 6 },
  exportText: { color: c.white, fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: c.surface, borderWidth: 1, borderColor: c.border },
  chipActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  chipTextActive: { color: c.white },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  countText: { fontSize: fontSize.sm, color: c.textSecondary },
  paginationInline: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pageText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
